import {
  type ConfirmarCadastroResposta,
  type PrepararCadastroResposta,
  type ValidarCodigoResposta,
} from '@varga/shared';
import { type FormEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  confirmarCadastro,
  type PrepararCadastroBody,
  prepararCadastro,
  validarCodigo,
} from '../api/cadastro';
import { CabecalhoTela } from '../components/Shell';
import { BlocoAviso, Botao, Campo, Chip, PlacaCodigo, Segmentado } from '../components/ui';
import { useToast } from '../components/toast';
import { MSG_ERRO_REDE, mensagemDeErro } from '../lib/erros';
import { ApiError } from '../lib/http';
import { lerRascunho, limparRascunho, salvarRascunho } from '../lib/session';

const CHAVE_RASCUNHO = 'cadastro';

interface FormState {
  numeroOs: string;
  nome: string;
  telefone: string;
  servico: string;
  valor: string;
  documento: string;
  placa: string;
  marca: string;
  modelo: string;
  ano: string;
  finalizadaEm: string;
  codigoErp: string;
  jaFezServico: 'nao' | 'sim' | 'nao_sei' | null;
  veioIndicado: 'sim' | 'nao' | null;
  codigoIndicacao: string;
}

const VAZIO: FormState = {
  numeroOs: '',
  nome: '',
  telefone: '',
  servico: '',
  valor: '',
  documento: '',
  placa: '',
  marca: '',
  modelo: '',
  ano: '',
  finalizadaEm: '',
  codigoErp: '',
  jaFezServico: null,
  veioIndicado: null,
  codigoIndicacao: '',
};

type Passo = 'preparar' | 'conferir' | 'resultado';

export function CadastroPagina(): ReactNode {
  const toast = useToast();
  const [passo, setPasso] = useState<Passo>('preparar');
  const [form, setForm] = useState<FormState>(
    () => lerRascunho<FormState>(CHAVE_RASCUNHO) ?? VAZIO,
  );
  const [erros, setErros] = useState<Partial<Record<keyof FormState, string>>>({});
  const [erroReq, setErroReq] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const [rascunho, setRascunho] = useState<PrepararCadastroResposta | null>(null);
  const [validacaoCodigo, setValidacaoCodigo] = useState<ValidarCodigoResposta | null>(null);
  const [validandoCodigo, setValidandoCodigo] = useState(false);
  const [resultado, setResultado] = useState<ConfirmarCadastroResposta | null>(null);

  const primeiroCampo = useRef<HTMLInputElement>(null);

  useEffect(() => {
    salvarRascunho(CHAVE_RASCUNHO, form);
  }, [form]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]): void => {
    setForm((f) => ({ ...f, [k]: v }));
    setErros((e) => ({ ...e, [k]: undefined }));
  };

  const clienteNovo = form.jaFezServico === 'nao';

  const reiniciar = (): void => {
    setForm(VAZIO);
    limparRascunho(CHAVE_RASCUNHO);
    setRascunho(null);
    setValidacaoCodigo(null);
    setResultado(null);
    setErroReq(null);
    setErros({});
    setPasso('preparar');
    primeiroCampo.current?.focus();
  };

  // ── Passo 1 → preparar ──────────────────────────────────────────────────────
  const validarPasso1 = (): boolean => {
    const e: Partial<Record<keyof FormState, string>> = {};
    if (!form.numeroOs.trim()) e.numeroOs = 'Informe o número da OS.';
    if (!form.nome.trim()) e.nome = 'Informe o nome.';
    if (!form.telefone.trim()) e.telefone = 'Informe o telefone.';
    if (!form.servico.trim()) e.servico = 'Descreva o serviço.';
    if (form.jaFezServico === null) e.jaFezServico = 'Escolha uma opção.';
    if (form.jaFezServico === 'nao_sei')
      e.jaFezServico =
        'Confirme com o cliente antes de cadastrar. Isso define se ele pode ser indicado e se ganha o brinde de boas-vindas.';
    if (form.veioIndicado === null) e.veioIndicado = 'Escolha uma opção.';
    if (form.veioIndicado === 'sim' && !form.codigoIndicacao.trim())
      e.codigoIndicacao = 'Informe o código de indicação.';
    setErros(e);
    return Object.keys(e).length === 0;
  };

  const montarBody = () => {
    const b: Record<string, unknown> = {
      numeroOs: form.numeroOs.trim(),
      nome: form.nome.trim(),
      telefone: form.telefone.trim(),
      servico: form.servico.trim(),
      clienteNovo,
    };
    if (form.valor.trim()) b.valor = Number(form.valor.replace(',', '.'));
    if (form.documento.trim()) b.documento = form.documento.trim();
    if (form.codigoErp.trim()) b.codigoErp = form.codigoErp.trim();
    if (form.finalizadaEm) b.finalizadaEm = form.finalizadaEm;
    if (form.placa.trim()) {
      const veiculo: Record<string, unknown> = { placa: form.placa.trim().toUpperCase() };
      if (form.marca.trim()) veiculo.marca = form.marca.trim();
      if (form.modelo.trim()) veiculo.modelo = form.modelo.trim();
      if (form.ano.trim()) veiculo.ano = Number(form.ano);
      b.veiculo = veiculo;
    }
    if (form.veioIndicado === 'sim' && form.codigoIndicacao.trim())
      b.codigoIndicacao = form.codigoIndicacao.trim().toUpperCase();
    return b as unknown as PrepararCadastroBody;
  };

  const aoPreparar = async (ev: FormEvent): Promise<void> => {
    ev.preventDefault();
    setErroReq(null);
    if (!validarPasso1()) return;
    setEnviando(true);
    try {
      const r = await prepararCadastro(montarBody());
      setRascunho(r);
      setPasso('conferir');
    } catch (err) {
      setErroReq(traduzir(err, { nome: form.nome }));
      if (err instanceof ApiError && err.codigo === 'telefone_invalido') {
        setErros((e) => ({ ...e, telefone: mensagemDeErro('telefone_invalido') }));
      }
      if (err instanceof ApiError && err.codigo === 'documento_invalido') {
        setErros((e) => ({ ...e, documento: mensagemDeErro('documento_invalido') }));
      }
    } finally {
      setEnviando(false);
    }
  };

  const aoValidarCodigo = async (): Promise<void> => {
    if (!form.codigoIndicacao.trim()) return;
    setValidandoCodigo(true);
    setValidacaoCodigo(null);
    try {
      const r = await validarCodigo(form.codigoIndicacao.trim().toUpperCase(), clienteNovo);
      setValidacaoCodigo(r);
    } catch (err) {
      setErros((e) => ({ ...e, codigoIndicacao: traduzir(err, { nome: form.nome }) }));
    } finally {
      setValidandoCodigo(false);
    }
  };

  // ── Passo 2 → confirmar ─────────────────────────────────────────────────────
  const aoConfirmar = async (): Promise<void> => {
    if (!rascunho) return;
    setErroReq(null);
    setEnviando(true);
    try {
      const r = await confirmarCadastro(rascunho.rascunhoId);
      setResultado(r);
      setPasso('resultado');
      limparRascunho(CHAVE_RASCUNHO);
    } catch (err) {
      if (err instanceof ApiError && err.codigo === 'nao_encontrado') {
        setErroReq('O rascunho expirou. Confira os dados e prepare de novo.');
        setPasso('preparar');
        setRascunho(null);
        return;
      }
      setErroReq(traduzir(err, { nome: form.nome }));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div>
      <CabecalhoTela titulo="Cadastro manual" />
      <div className="max-w-form">
        {erroReq && (
          <div aria-live="assertive" className="mb-4">
            <BlocoAviso tom="erro">{erroReq}</BlocoAviso>
          </div>
        )}

        {passo === 'preparar' && (
          <Passo1
            form={form}
            erros={erros}
            set={set}
            enviando={enviando}
            primeiroCampo={primeiroCampo}
            onSubmit={(e) => void aoPreparar(e)}
            validacaoCodigo={validacaoCodigo}
            validandoCodigo={validandoCodigo}
            onValidarCodigo={() => void aoValidarCodigo()}
          />
        )}

        {passo === 'conferir' && rascunho && (
          <Passo2
            rascunho={rascunho}
            validacaoCodigo={validacaoCodigo}
            enviando={enviando}
            onVoltar={() => setPasso('preparar')}
            onConfirmar={() => void aoConfirmar()}
          />
        )}

        {passo === 'resultado' && resultado && (
          <Passo3
            resposta={resultado}
            nome={form.nome}
            numeroOs={form.numeroOs}
            onNovo={reiniciar}
            toast={toast}
          />
        )}
      </div>
    </div>
  );
}

function traduzir(err: unknown, vars: { nome?: string }): string {
  if (err instanceof ApiError) {
    if (err.ehRede) return MSG_ERRO_REDE;
    return mensagemDeErro(err.codigo, vars);
  }
  return MSG_ERRO_REDE;
}

// ─────────────────────────────────────────────────────────────────────────────
interface Passo1Props {
  form: FormState;
  erros: Partial<Record<keyof FormState, string>>;
  set: <K extends keyof FormState>(k: K, v: FormState[K]) => void;
  enviando: boolean;
  primeiroCampo: React.RefObject<HTMLInputElement>;
  onSubmit: (e: FormEvent) => void;
  validacaoCodigo: ValidarCodigoResposta | null;
  validandoCodigo: boolean;
  onValidarCodigo: () => void;
}

function Passo1(p: Passo1Props): ReactNode {
  const { form, erros, set } = p;
  return (
    <form onSubmit={p.onSubmit} className="flex flex-col gap-4" noValidate>
      <Campo
        ref={p.primeiroCampo}
        rotulo="Número da OS"
        obrigatorio
        value={form.numeroOs}
        erro={erros.numeroOs}
        onChange={(e) => set('numeroOs', e.target.value)}
      />
      <Campo
        rotulo="Nome"
        obrigatorio
        value={form.nome}
        erro={erros.nome}
        onChange={(e) => set('nome', e.target.value)}
      />
      <Campo
        rotulo="Telefone"
        obrigatorio
        ajuda="pode digitar com DDD, a gente formata"
        value={form.telefone}
        erro={erros.telefone}
        onChange={(e) => set('telefone', e.target.value)}
      />
      <Campo
        rotulo="Serviço"
        obrigatorio
        value={form.servico}
        erro={erros.servico}
        onChange={(e) => set('servico', e.target.value)}
      />
      <Campo
        rotulo="Valor"
        inputMode="decimal"
        value={form.valor}
        onChange={(e) => set('valor', e.target.value)}
      />
      <Campo
        rotulo="Documento (CPF/CNPJ)"
        ajuda="opcional — ajuda a barrar autoindicação por documento"
        value={form.documento}
        erro={erros.documento}
        onChange={(e) => set('documento', e.target.value)}
      />
      <div className="grid grid-cols-2 gap-3">
        <Campo rotulo="Placa" value={form.placa} onChange={(e) => set('placa', e.target.value)} />
        <Campo
          rotulo="Ano"
          inputMode="numeric"
          value={form.ano}
          onChange={(e) => set('ano', e.target.value)}
        />
        <Campo rotulo="Marca" value={form.marca} onChange={(e) => set('marca', e.target.value)} />
        <Campo
          rotulo="Modelo"
          value={form.modelo}
          onChange={(e) => set('modelo', e.target.value)}
        />
      </div>
      <Campo
        rotulo="Data da OS"
        type="date"
        value={form.finalizadaEm}
        onChange={(e) => set('finalizadaEm', e.target.value)}
      />
      <Campo
        rotulo="Código no ERP"
        ajuda="o CLIENTE CODIGO:NN da OS — não é código de indicação"
        value={form.codigoErp}
        onChange={(e) => set('codigoErp', e.target.value)}
      />

      <Segmentado
        rotulo="Este cliente já fez serviço na Varga antes?"
        opcoes={[
          { valor: 'nao', texto: 'Não' },
          { valor: 'sim', texto: 'Sim' },
          { valor: 'nao_sei', texto: 'Não sei' },
        ]}
        valor={form.jaFezServico}
        aoMudar={(v) => set('jaFezServico', v)}
        erro={erros.jaFezServico}
      />

      <Segmentado
        rotulo="Veio indicado por alguém?"
        opcoes={[
          { valor: 'sim', texto: 'Sim, tenho o código' },
          { valor: 'nao', texto: 'Não / sem indicação' },
        ]}
        valor={form.veioIndicado}
        aoMudar={(v) => set('veioIndicado', v)}
        erro={erros.veioIndicado}
      />

      {form.veioIndicado === 'sim' && (
        <div className="flex flex-col gap-2 rounded-2 border border-linha bg-superficie p-3">
          <div className="flex items-end gap-2">
            <Campo
              rotulo="Código de indicação"
              className="flex-1 font-codigo"
              value={form.codigoIndicacao}
              erro={erros.codigoIndicacao}
              onChange={(e) => set('codigoIndicacao', e.target.value.toUpperCase())}
            />
            <Botao
              type="button"
              variante="secundario"
              carregando={p.validandoCodigo}
              onClick={p.onValidarCodigo}
            >
              Validar código
            </Botao>
          </div>
          {p.validacaoCodigo?.valido && (
            <p className="text-nota text-sucesso">
              Código de {primeiroNome(p.validacaoCodigo.indicador.nome)} — a indicação vai
              converter.
            </p>
          )}
          {p.validacaoCodigo && !p.validacaoCodigo.valido && (
            <p className="text-nota text-erro">
              {mensagemDeErro(p.validacaoCodigo.motivo, { nome: form.nome })}
            </p>
          )}
        </div>
      )}

      <Botao type="submit" carregando={p.enviando}>
        {p.enviando ? 'Preparando…' : 'Conferir dados'}
      </Botao>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function Passo2({
  rascunho,
  validacaoCodigo,
  enviando,
  onVoltar,
  onConfirmar,
}: {
  rascunho: PrepararCadastroResposta;
  validacaoCodigo: ValidarCodigoResposta | null;
  enviando: boolean;
  onVoltar: () => void;
  onConfirmar: () => void;
}): ReactNode {
  const d = rascunho.dados;
  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-2 border border-linha bg-superficie p-4">
        <h2 className="font-display text-bloco-titulo font-bold uppercase text-tinta-forte">
          Conferir
        </h2>
        <dl className="mt-3 grid grid-cols-[10rem_1fr] gap-x-3 gap-y-1.5 text-corpo">
          <Linha rotulo="Nome" valor={d.nome} />
          <Linha rotulo="Telefone" valor={d.telefone} mono />
          <Linha rotulo="Número da OS" valor={d.numeroOs} mono />
          <Linha rotulo="Serviço" valor={d.servico} />
          {d.valor !== undefined && <Linha rotulo="Valor" valor={`R$ ${d.valor}`} mono />}
          {d.documento && <Linha rotulo="Documento" valor={d.documento} mono />}
          {d.veiculo && (
            <Linha
              rotulo="Veículo"
              valor={[d.veiculo.placa, d.veiculo.marca, d.veiculo.modelo, d.veiculo.ano]
                .filter(Boolean)
                .join(' · ')}
            />
          )}
          {d.codigoErp && <Linha rotulo="Código no ERP" valor={`ERP #${d.codigoErp}`} />}
          <Linha
            rotulo="Já era cliente da Varga"
            valor={d.clienteNovo ? 'Não (cliente novo)' : 'Sim'}
          />
          {d.codigoIndicacao && (
            <Linha rotulo="Código de indicação" valor={d.codigoIndicacao} mono />
          )}
        </dl>
      </div>

      {rascunho.avisos.length > 0 && (
        <BlocoAviso tom="aviso">
          <ul className="list-disc pl-4">
            {rascunho.avisos.map((a, i) => (
              <li key={i}>{a}</li>
            ))}
          </ul>
        </BlocoAviso>
      )}

      {d.codigoIndicacao && (
        <div className="rounded-2 border border-linha bg-superficie p-3 text-corpo">
          {validacaoCodigo?.valido ? (
            <p>
              Indicação de <strong>{primeiroNome(validacaoCodigo.indicador.nome)}</strong> →
              converte. Brinde do indicador: nível {validacaoCodigo.nivelIndicador}.
              {d.clienteNovo && ' Brinde do indicado: Cristalização do vidro.'}
            </p>
          ) : validacaoCodigo && !validacaoCodigo.valido ? (
            <p>
              A indicação NÃO vai contar: {mensagemDeErro(validacaoCodigo.motivo, { nome: d.nome })}{' '}
              O cliente e a OS entram normalmente; sem brinde de indicação.
            </p>
          ) : (
            <p className="text-tinta-2">
              O resultado da indicação é conferido ao confirmar. Use "Validar código" no passo
              anterior para saber antes.
            </p>
          )}
        </div>
      )}

      <p className="text-nota text-tinta-2">
        Ao confirmar, você registra o cadastro. As concessões de brinde não podem ser desfeitas.
      </p>
      <div className="flex gap-2">
        <Botao variante="secundario" onClick={onVoltar}>
          Voltar
        </Botao>
        <Botao carregando={enviando} onClick={onConfirmar}>
          {enviando ? 'Confirmando…' : 'Confirmar cadastro'}
        </Botao>
      </div>
    </div>
  );
}

function Linha({
  rotulo,
  valor,
  mono,
}: {
  rotulo: string;
  valor: string;
  mono?: boolean;
}): ReactNode {
  return (
    <>
      <dt className="text-tinta-2">{rotulo}</dt>
      <dd className={mono ? 'tnum text-tinta-forte' : 'text-tinta-forte'}>{valor}</dd>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function Passo3({
  resposta,
  nome,
  numeroOs,
  onNovo,
  toast,
}: {
  resposta: ConfirmarCadastroResposta;
  nome: string;
  numeroOs: string;
  onNovo: () => void;
  toast: (t: string) => void;
}): ReactNode {
  if (resposta.idempotente) {
    return (
      <div className="flex flex-col gap-4">
        <div className="rounded-2 border border-linha bg-superficie p-4 text-corpo text-tinta-forte">
          A OS {numeroOs} já tinha sido cadastrada. Nada foi duplicado.
        </div>
        <div className="flex gap-2">
          <Link
            to={`/clientes/${resposta.cliente.id}`}
            className="inline-flex h-10 items-center rounded-1 border border-linha-forte bg-superficie-alta px-4 font-display text-[0.9375rem] font-bold text-tinta-forte"
          >
            Ver ficha do cliente
          </Link>
          <Botao onClick={onNovo}>Novo cadastro</Botao>
        </div>
      </div>
    );
  }

  const indic = resposta.indicacao;
  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-2 border border-linha bg-superficie-alta p-4">
        <h2 className="font-display text-bloco-titulo font-bold uppercase text-tinta-forte">
          Cadastro feito
        </h2>
        <p className="mt-2 text-corpo text-tinta">
          {resposta.cliente.nome} {resposta.cliente.novo ? '(cliente novo)' : '(já era cadastrado)'}
        </p>
        <div className="mt-3 flex items-center gap-2">
          <span className="text-rotulo uppercase text-tinta-2">Código de indicação</span>
          <PlacaCodigo codigo={resposta.codigo} />
        </div>
      </div>

      {indic?.status === 'convertida' && (
        <div className="rounded-2 border border-sucesso bg-sucesso-fraco p-4 text-corpo text-tinta-forte">
          <p>A indicação converteu. Vouchers concedidos:</p>
          <ul className="mt-2 flex flex-col gap-1">
            {resposta.vouchers.map((v) => (
              <li key={v.voucherCodigo} className="flex items-center gap-2">
                <Chip tom="acento">{v.papel === 'indicador' ? 'Indicador' : 'Boas-vindas'}</Chip>
                <span>{v.descricao}</span>
                <span className="font-codigo text-[0.8125rem]">{v.voucherCodigo}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {indic?.status === 'rejeitada' && (
        <BlocoAviso tom="aviso">
          A indicação foi registrada como rejeitada
          {indic.motivo ? ` (${mensagemDeErro(indic.motivo, { nome })})` : ''}. O cadastro do
          cliente e da OS foi feito normalmente.
        </BlocoAviso>
      )}

      {resposta.linkWa && (
        <div className="rounded-2 border border-linha bg-superficie p-3 text-corpo">
          <a
            href={resposta.linkWa}
            target="_blank"
            rel="noopener noreferrer"
            className="font-display text-[0.9375rem] font-bold text-acento-texto underline"
            onClick={() => toast('Abrindo o WhatsApp…')}
          >
            Abrir no WhatsApp
          </a>
          <p className="mt-1 text-nota text-tinta-2">
            A notificação também está na fila de Notificações.
          </p>
        </div>
      )}

      <div className="flex gap-2">
        <Link
          to={`/clientes/${resposta.cliente.id}`}
          className="inline-flex h-10 items-center rounded-1 border border-linha-forte bg-superficie-alta px-4 font-display text-[0.9375rem] font-bold text-tinta-forte"
        >
          Ver ficha
        </Link>
        <Botao onClick={onNovo}>Novo cadastro</Botao>
      </div>
    </div>
  );
}

function primeiroNome(nome: string): string {
  return nome.trim().split(/\s+/)[0] ?? nome;
}
